import Fuse from 'fuse.js'

interface Place {
  name: string;
  region: string;
  isItalian: boolean;
  grid: number;
}

const fuseOptions = {
  keys: ['name'],
  threshold: 0.2
}

const dataCache = new Map<string,Promise<Place[]>>()
const fuseCache = new WeakMap<Place[], Fuse<Place, {keys: string[]}>>()
let reverseCache: Promise<Map<number,Place>> | undefined = undefined

class Reader {
  buffer: ArrayBuffer
  decoder: TextDecoder
  bytes: Uint8Array
  index: number
  regions: string[]

  readByte(): number {
    return this.bytes[this.index++]
  }

  readUInt16(): number {
    const end = this.index + 2
    const num = new Uint16Array(this.buffer.slice(this.index, end))[0]
    this.index = end
    return num
  }
  readUInt32(): number {
    const end = this.index + 4
    const num = new Uint32Array(this.buffer.slice(this.index, end))[0]
    this.index = end
    return num
  }

  readLon() {
    return 5 + (this.readUInt16()+0.5)*15/65536
  }

  readLat() {
    return 36 + (this.readUInt16()+0.5)*12/65536
  }

  readOlsiGrid() {
    return this.readUInt32()
  }

  readString(): string {
    const len = this.readByte()
    const end = this.index + len
    const str = this.decoder.decode(this.buffer.slice(this.index, end))
    this.index = end
    return str
  }

  constructor(buffer: ArrayBuffer)  {
    this.index = 0
    this.buffer = buffer
    this.bytes = new Uint8Array(buffer)
    this.decoder = new TextDecoder('utf-8')
    this.regions = []
  }

  readPlace(): Place {
    const name = this.readString()
    const regionWithFlag = this.readByte()
    const regionIndex = regionWithFlag & 127
    const flag = (regionWithFlag & 128) > 0
    const grid = this.readOlsiGrid()
    //const lon = this.readLon()
    //const lat = this.readLat()

    return {
      name, 
      region: this.regions[regionIndex],
      isItalian: flag,
      grid
    }
  }

  get() {
    const regionCount = this.readByte()
    
    for(let i = 0; i < regionCount; i++) {
      this.regions.push(this.readString())
    }
    
    const places = []
    while(this.index < this.buffer.byteLength) {
      places.push(this.readPlace())
    }
    return places
  }
}



function loadDataset(dataset: "CH" | "IT" | string, italianOnly = false): Promise<Place[]> {
  dataset = dataset.toLowerCase()
  const k = dataset + (italianOnly ? 'i' : '')

  if(!dataCache.has(k)) {
    dataCache.set(k, new Promise<Place[]>((resolve) => {
      if (!dataCache.has(dataset)) {
        dataCache.set(dataset, new Promise<Place[]>((resolve) => {
          fetch(`/data/${dataset}.bin`)
            .then(response => response.arrayBuffer())
            .then(buffer => new Reader(buffer))
            .then(reader => {
              const places = reader.get()
              resolve(places)
            })
        }))
      }

      if (italianOnly && !dataCache.has(k)) {
        const places = (dataCache.get(dataset) || Promise.resolve([])).then(p => p.filter(p => p.isItalian))
        dataCache.set(k, places)
        resolve(places)
      } else {
        resolve(dataCache.get(k) || [])
      }
    }))
  }

  return dataCache.get(k) || Promise.resolve([])
}

async function loadDatasets(datasets: Array<"CH" | "IT">, italianOnly = false): Promise<Place[]> {
  const allPlaces = await Promise.all(datasets.map(ds => loadDataset(ds, italianOnly)))
  return allPlaces.flat(1)
}

async function findPlace(query: string, dataset: "CH" | "IT" | Array<"CH" | "IT">, italianOnly = false) {
  const places = await (Array.isArray(dataset)
    ? loadDatasets(dataset, italianOnly)
    : loadDataset(dataset, italianOnly))
  
  let fuse;

  if(undefined === (fuse = fuseCache.get(places))) {
    fuse = new Fuse(places, fuseOptions)
    fuseCache.set(places, fuse)
  }
  
  return fuse.search(query).map(fo => fo.item)
}

function reverseLookup(grid: number) {
  if(typeof grid !== 'number') {
    grid = parseInt(grid)
  }
  return new Promise(resolve => {
    if(!reverseCache) {
      reverseCache = Promise.all([loadDataset('CH'), loadDataset('IT')]).then(datasets => {
        const allPlaces = datasets.flat(1)
        const map = new Map<number,Place>()
        for (const place of allPlaces) {
          map.set(place.grid, place)
        }
        return map
      })
    }
    reverseCache.then(map => {
      const place = map.get(grid)
      resolve(place)
    })
  })  
}

export { findPlace, loadDataset, reverseLookup };